import { useFloatingPanel } from '@/components/admin/protocoles/step/floating-panel';
import { VersionPill } from '@/components/admin/versioned/version-pill';
import { useAdminSession } from '@/lib/admin-session';
import { fetchCriterion } from '@/lib/api';
import { exampleCountLabel, scoringPillLabel } from '@/lib/criterion-labels';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The fiche a criterion's name opens on the line of an objective (refonte
 * modulaire §7.2; maquette `cpopHTML`; grill of 2026-09-21, issue #104): where
 * the direction comes from, the direction itself, what feeds the scribe, and a
 * way out to its page.
 *
 * ## It is a COMPONENT of its own so that the read is lazy
 *
 * The row that opens it renders once per cited criterion, and a step can cite
 * ten. Reading each of them at mount would be ten requests for a card nobody
 * asked to see - so the query lives here, and here is mounted only while the
 * fiche is open. That is `enabled: open` written as a mounting rather than as
 * a flag, and it buys one more thing: the step editor stays mountable outside
 * the shell, because `useAdminSession` and `useQuery` are only reached by a
 * click.
 *
 * The KEY is the criterion editor's, `['criterion', email, id]`, deliberately:
 * two components sharing a cache entry must agree on its SHAPE, nothing checks
 * that they do, and the 30 s `staleTime` of `query-client.ts` means whichever
 * screen read it first is the one the other renders. One key, one shape -
 * `CriterionDetail` on both sides.
 *
 * ## A CLICK, and therefore a dialog (D1, D2)
 *
 * The name opens it and nothing else does. A panel that opened on hover would
 * have to hold a grace period wide enough to cross the gap to it, because it
 * carries a link one has to be able to reach - and a grace period still loses
 * whoever cuts the corner. The spec says the gesture outright, « le nom, qui
 * ouvre sa fiche ».
 *
 * Opened by a click and carrying an action, it owes the contract of a
 * NON-MODAL dialog, which is what D2 settles: `role="dialog"` here,
 * `aria-haspopup="dialog"` and `aria-expanded` on the name, Escape closes and
 * hands the focus back, a click outside closes. Nothing traps the focus and
 * nothing is inert behind it - it is a panel beside the page, not in front of
 * it. Before this lot it closed only by clicking the name a second time, which
 * left a keyboard with no way out of it at all.
 *
 * No arrow, and that is the design system rather than an omission: an arrow is
 * the signature of a bubble one does not click, and this one is clicked.
 *
 * ## Two regimes, because two places read the fiche of a criterion
 *
 * `variable-fiche.tsx` mounts this component for a `@critere` pill, « une
 * fiche pour un critère, où qu'on la lise ». That fiche is ALREADY a placed
 * `fixed` box - it opens on hover, it is a `role="note"`, and
 * `useVariableFiche` owns its Escape and its outside click. So the panel takes
 * its anchor as a prop and reads it as the question « do I place myself »:
 *
 * - **with an anchor** (the row of an objective): it draws itself `fixed`,
 *   placed by `useFloatingPanel`, and owns the dialog contract above;
 * - **without one** (`variable-fiche.tsx`): it draws the card and nothing
 *   else, in the flow of the box its caller has already placed and named. A
 *   second `role` inside that box would claim a dialog where the caller has
 *   deliberately promised a note, and a second placement would fight the
 *   first.
 *
 * ## What it says, and in WHICH ORDER (D6)
 *
 * The order changed and no fact was dropped. It used to open on the name, then
 * a dense line of four items, then the direction: what one comes to read was
 * under everything one does not. Now the name carries, on its own line and
 * aligned right, the version the direction actually COMES FROM - a direction
 * read without knowing its version can mislead - the direction follows
 * immediately, and the rest falls under a rule: the production pointer, how
 * many examples feed the scribe, and the notation.
 *
 * The version named beside the name is the DEVELOPMENT one, and it is not a
 * choice between the two pointers: `CriterionDetail` carries
 * `developmentContent` and no other content, so the direction below is the
 * development version's, always, in both regimes. Naming the citation's own
 * tag there would be the exact mistake D6 exists to prevent - « production
 * v3 » over a direction written in v5. The production pointer keeps its pill
 * under the rule, where it answers a different question, and §7.2's « pastilles
 * des deux tags » is still honoured.
 *
 * The production pill is ABSENT when nothing was ever passed (§4), and what
 * takes its place here is a neutral « jamais en production » rather than
 * nothing: on a card the absence is read against its neighbours, but a fiche
 * shows one criterion and an absent pill would be a blank the eye cannot
 * interpret.
 *
 * ## A direction may be 4000 characters, so it SCROLLS (D4)
 *
 * H21 caps a step's direction at 4000 characters and a criterion's is no
 * shorter in practice. The fiche therefore caps the direction's own height and
 * scrolls it, rather than growing into a panel taller than the window: a panel
 * one opened on purpose may scroll, because the pointer can go there - which
 * is exactly what is not true of an infobulle. Capping the DIRECTION and not
 * the whole fiche keeps the pills, the rule and « Ouvrir le critère » on
 * screen whatever the direction's length.
 */

export interface CriterionCardPopoverProps {
  readonly criterionId: string;
  /**
   * What the fiche stands against, and the control the focus returns to. Left
   * out by a caller that has already placed the fiche itself - see the two
   * regimes above.
   */
  readonly anchor?: HTMLElement | null;
  /** Escape, or a click outside. Never reached in the anchorless regime. */
  readonly onClose?: () => void;
}

/** The maquette's `w-95`, needed as a number before the DOM has measured one. */
export const CRITERION_FICHE_WIDTH = 380;

const CARD_CLASS = 'rounded-md border-2 border-gray-200 bg-white p-4 shadow-md';

export function CriterionCardPopover({
  criterionId,
  anchor = null,
  onClose,
}: CriterionCardPopoverProps) {
  const { email } = useAdminSession();
  const criterion = useQuery({
    queryKey: ['criterion', email, criterionId],
    queryFn: () => fetchCriterion(criterionId),
    retry: false,
  });
  const { panelRef, at } = useFloatingPanel({
    anchor,
    width: CRITERION_FICHE_WIDTH,
    onClose: onClose ?? (() => undefined),
  });

  const body: ReactNode = (
    <>
      {criterion.isPending ? <p className="text-gray-600 text-sm">Lecture du critère…</p> : null}
      {criterion.isError ? (
        <p className="text-gray-700 text-sm">Ce critère n’a pas pu être lu.</p>
      ) : null}
      {criterion.data === undefined ? null : (
        <>
          {/*
            The name and, on its line, the version the direction below comes
            from (D6). `items-start` and not `items-center`: a name long enough
            to wrap must push the pill against its FIRST line, not float it
            against the middle of two.
          */}
          <div className="flex items-start gap-3">
            <h4 className="min-w-0 flex-1 font-semibold text-base text-bleu-nuit">
              {criterion.data.name}
            </h4>
            <VersionPill
              entity="critere"
              tag="development"
              version={criterion.data.development.number}
              className="mt-0.5 shrink-0"
            />
          </div>
          {/*
            Focusable because it SCROLLS (D4). A region a pointer can reach and
            a keyboard cannot is a direction a keyboard user reads the first ten
            lines of; `tabIndex` is what gives it the arrow keys, and the ring
            is what says it has taken the focus.
          */}
          <section
            /*
              Focalisable SEULEMENT en régime dialogue, et c'est une correction
              de revue. Ce même composant est monté par la fiche d'une pastille
              de variable, qui s'ouvre au SURVOL et disparaît quand le pointeur
              s'en va : un arrêt de tabulation y serait un arrêt qui se détruit
              avant qu'on l'atteigne. Ouverte au clic, elle reste, et la région
              doit alors être atteignable - une zone qu'un pointeur fait défiler
              et qu'un clavier ne peut pas, c'est une direction de 4000
              caractères dont on lit les dix premières lignes.
            */
            /*
              Un `section` NOMMÉ et non un `p` : un paragraphe a un rôle sur
              lequel le nommage est interdit, donc l'étiquette y était
              simplement ignorée et un lecteur clavier tombait dans une zone
              défilante anonyme. Un `section` porte nativement le rôle `region`,
              qui se nomme.
            */
            // biome-ignore lint/a11y/noNoninteractiveTabindex: the rule guards against tab stops that do nothing; this one scrolls.
            tabIndex={anchor === null ? undefined : 0}
            aria-label={`Direction de « ${criterion.data.name} »`}
            className="mt-2.5 max-h-56 overflow-y-auto text-gray-800 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {criterion.data.developmentContent.direction}
          </section>
          <div className="mt-2.5 flex flex-wrap items-center gap-2 border-gray-200 border-t pt-2.5 text-gray-600 text-xs">
            {criterion.data.production === null ? (
              <span className="inline-flex items-center rounded-pill bg-gray-100 px-2.5 py-0.5 font-semibold text-gray-600 text-xs leading-4">
                jamais en production
              </span>
            ) : (
              <VersionPill
                entity="critere"
                tag="production"
                version={criterion.data.production.number}
              />
            )}
            <span>· {exampleCountLabel(criterion.data.developmentContent.examples.length)}</span>
            {/*
              The scoring pill, and NOTHING when there is none (§6): an absence
              does not display, here as on the card. What it says that the card
              does not is the plancher, because this fiche is read from the line
              of an objective - the one place where a threshold can replace it
              (§7.2).
            */}
            {criterion.data.developmentContent.scoring === null ? null : (
              <span className="inline-flex items-center rounded-pill bg-bleu-nuit-tint px-2.5 py-0.5 font-semibold text-bleu-nuit text-xs leading-4">
                {scoringPillLabel(criterion.data.developmentContent.scoring)}
              </span>
            )}
          </div>
          <Link
            to="/admin/criteres/$criterionId"
            params={{ criterionId }}
            target="_blank"
            className="mt-3 inline-flex items-center gap-1.5 font-semibold text-bleu-nuit text-sm underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Ouvrir le critère
            <ExternalLink aria-hidden="true" className="size-3.5" />
          </Link>
        </>
      )}
    </>
  );

  /*
    The anchorless regime: the caller's box IS the panel - it carries the
    placement, the role and the name - so all that is owed here is the card.
  */
  if (anchor === null) return <div className={`w-full ${CARD_CLASS}`}>{body}</div>;

  return (
    <div
      ref={panelRef}
      // biome-ignore lint/a11y/useSemanticElements: a `<dialog open>` carries the UA's own absolute placement and margins, which would fight the `fixed` coordinates this panel is placed at; and `showModal` is the wrong promise - D2 asks for a NON-modal dialog, nothing inert behind it.
      role="dialog"
      aria-label={
        criterion.data === undefined
          ? 'Fiche du critère'
          : `Fiche du critère « ${criterion.data.name} »`
      }
      style={{ left: at.left, top: at.top, width: CRITERION_FICHE_WIDTH }}
      className={`fixed z-30 max-w-[calc(100vw-16px)] text-left ${CARD_CLASS}`}
    >
      {body}
    </div>
  );
}
